import type { TenantDetail, TenantOnboardingInput, TenantStatus } from '@/types/platform';
import { seedTenant, slugify, type TenantData } from '@/data/adminSeed';
import { buildReport } from '../admin/insights';
import { ServiceError } from '../../contracts';
import type { PlatformTenantService } from '../../platformContracts';
import { adminRoot, dataFor, delay, matches, paginate, pctx, planOf, recordAudit, saveAdmin, syncTenantFeatures, tenantRow, uid } from './context';

const invalid = (m: string) => new ServiceError(m, 'INVALID');
const STATUSES: TenantStatus[] = ['ACTIVE', 'TRIAL', 'SUSPENDED'];

function detail(t: TenantData): TenantDetail {
  const r = buildReport(t, '30d');
  return {
    tenantId: t.tenantId, name: t.name, slug: t.slug, status: t.status, planId: t.planId, ownerName: t.ownerName, ownerEmail: t.ownerEmail,
    createdAt: t.createdAt, logoUrl: t.branding.logoUrl,
    stats: { customers: t.customers.length, products: t.products.length, orders30d: r.kpis.orders.value, revenue30d: r.kpis.revenue.value, revenuePrev30d: r.kpis.revenue.previous },
  };
}

function hash(s: string) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(h, 31) + s.charCodeAt(i)) | 0;
  return Math.abs(h) || 1;
}

export const platformTenants: PlatformTenantService = {
  async list(q) {
    pctx('platform.tenants.read');
    const rows = Object.values(adminRoot())
      .filter((t) => (!q.status || q.status === 'all' || t.status === q.status) && (!q.planId || t.planId === q.planId) && matches(q.q, t.name, t.slug, t.ownerEmail, t.ownerName))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      .map(tenantRow);
    return delay(paginate(rows, q.page, q.pageSize ?? 10));
  },
  async get(id) {
    pctx('platform.tenants.read');
    return delay(detail(dataFor(id)));
  },
  async create(input) {
    const { actor } = pctx('platform.tenants.manage');
    if (!input.name.trim()) throw invalid('Store name is required');
    const slug = slugify(input.slug || input.name);
    if (!slug) throw invalid('Enter a valid store URL slug');
    if (Object.values(adminRoot()).some((t) => t.slug === slug)) throw new ServiceError('A store with this URL slug already exists', 'CONFLICT');
    if (!/^\S+@\S+\.\S+$/.test(input.ownerEmail)) throw invalid('Enter a valid owner email');
    if (!input.ownerName.trim()) throw invalid('Owner name is required');
    let plan;
    try { plan = planOf(input.planId); } catch { throw invalid('Choose a valid plan'); }
    if (!plan.active) throw invalid('This plan is not available for new stores');
    if (!STATUSES.includes(input.status)) throw invalid('Choose a valid status');

    const tenantId = `tenant_${slug.replace(/-/g, '')}_${Date.now().toString(36)}`;
    const t = seedTenant({
      tenantId, name: input.name.trim(), slug, seed: hash(slug), productCount: 5, customerCount: 4, orderCount: 6,
      ownerName: input.ownerName.trim(), ownerEmail: input.ownerEmail.trim().toLowerCase(), prefix: slug.slice(0, 3).toUpperCase() || 'STR',
      planId: plan.id, status: input.status, createdAt: new Date().toISOString(),
    });
    adminRoot()[tenantId] = t;
    saveAdmin();
    recordAudit(actor, 'tenant.created', 'TENANT', tenantId, t.name, `Created "${t.name}" on the ${plan.name} plan (${input.status.toLowerCase()})`);
    return delay(detail(t), 700);
  },
  async setStatus(id, status, reason) {
    const { actor } = pctx('platform.tenants.manage');
    if (!STATUSES.includes(status)) throw invalid('Invalid status');
    const t = dataFor(id);
    const from = t.status;
    if (from === status) return delay(detail(t), 200);
    t.status = status;
    saveAdmin();
    recordAudit(actor, 'tenant.status_changed', 'TENANT', id, t.name, `Status changed from ${from} to ${status}${reason ? ` — ${reason}` : ''}`);
    return delay(detail(t), 500);
  },
  async setPlan(id, planId) {
    const { actor } = pctx('platform.tenants.manage');
    const t = dataFor(id);
    let plan;
    try { plan = planOf(planId); } catch { throw invalid('Choose a valid plan'); }
    const from = t.planId;
    if (from === planId) return delay(detail(t), 200);
    t.planId = planId;
    syncTenantFeatures(id);
    recordAudit(actor, 'tenant.plan_changed', 'TENANT', id, t.name, `Plan changed from ${planOf(from).name} to ${plan.name}`);
    return delay(detail(t), 500);
  },
};

export { hash as slugHash, uid };
